import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { compact } from "lodash-es"

import { CorePaymentMethod } from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"

import { DotDropdown, type DropdownValue } from "@oysterhr/feature-components"

import { CompanyPaymentMethodCardLayout } from "../CompanyPaymentMethodCardLayout"
import {
  isAuthorizable,
  isAchAndVerifiable,
  showDirectDebitMandate,
  isSepaOrBacsAccount,
  isDeactivated,
  isReady,
} from "../../utils"
import { useHandleAuthorize } from "../../actions"
import { CreditorsIdModal } from "../../modals/CreditorsIdModal"

interface CompanyBankPaymentMethodCardProps {
  paymentMethod: CorePaymentMethod
  stateLoading: boolean
  setShowDeleteModal: (show: boolean) => void
}

export const CompanyBankPaymentMethodCard = ({
  paymentMethod,
  stateLoading,
  setShowDeleteModal,
}: CompanyBankPaymentMethodCardProps) => {
  const { t } = useTranslation(
    "company_interface/CompanyPaymentMethodsPage/CompanyBankPaymentMethodCard",
  )
  const navigate = useNavigate()

  const [showCreditorsIdModal, setShowCreditorsIdModal] = useState(false)

  const { handleAuthorize } = useHandleAuthorize(paymentMethod)

  const deactivatedPayment = isDeactivated(paymentMethod)

  const actions = useMemo<DropdownValue[]>(() => {
    return compact([
      isAuthorizable(paymentMethod) && {
        title: t("actions.authorize"),
        onClick: handleAuthorize,
      },
      isAchAndVerifiable(paymentMethod) && {
        title: t("actions.verify"),
        onClick: () => {
          navigate("verify-stripe-ach-payment-method", {
            state: {
              paymentMethod: { ...paymentMethod },
            },
          })
        },
      },
      isReady(paymentMethod) &&
        paymentMethod.__typename === PaymentMethodEnumType.STRIPE_ACH && {
          title: t("actions.seeCreditorsId"),
          onClick: () => setShowCreditorsIdModal(true),
        },
      isSepaOrBacsAccount(paymentMethod) &&
        showDirectDebitMandate(paymentMethod) && {
          title: t("actions.seeDirectDebitMandate"),
          onClick: () => window.open(paymentMethod.mandateUrl as string, "_blank"),
        },
      {
        title: t("actions.delete"),
        danger: true,
        onClick: () => setShowDeleteModal(true),
      },
    ])
  }, [paymentMethod, setShowDeleteModal, handleAuthorize, t, navigate])

  return (
    <div>
      <CompanyPaymentMethodCardLayout
        stateLoading={stateLoading}
        paymentMethod={paymentMethod}
        isDeactivated={deactivatedPayment}
      >
        {/* Actions in the dropdown */}
        <DotDropdown
          dropdownValues={actions}
          ariaLabel={"actions"}
          containerStyle="w-auto whitespace-nowrap"
        />
      </CompanyPaymentMethodCardLayout>

      <CreditorsIdModal
        paymentMethod={paymentMethod}
        isOpen={showCreditorsIdModal}
        closeModal={() => setShowCreditorsIdModal(false)}
      />
    </div>
  )
}
